import { Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { round } from 'lodash';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useArticlesQueries } from '~/lib/api/modules/Articles/hooks/useArticleQueries';
import { useIapPollResultsQuery } from '~/lib/api/modules/IapPolls/hooks/useIapPollResultsQuery';

export type IIapPollStatsProps = IContainerProps & {
  appId: string;
  iapPollId: string;
};

const styles = stylex.create({
  results: {
    borderLeftWidth: outlineTokens.width$xs,
    borderLeftStyle: 'solid',
    borderLeftColor: colorSchemeTokens.outline,
    paddingTop: spacingTokens.padding$4,
    paddingBottom: spacingTokens.padding$4,
    display: 'flex',
    flexDirection: 'column',
    gap: spacingTokens.padding$2,
  },
  result: {
    height: spacingTokens.padding$8,
    color: colorSchemeTokens.onSurface,
    display: 'flex',
  },
  progression: (progression: number) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: spacingTokens.padding$2,
    backgroundColor: colorSchemeTokens.outlineVariant,
    borderTopRightRadius: shapeTokens.corner$xs,
    borderBottomRightRadius: shapeTokens.corner$xs,
    overflow: 'visible',
    whiteSpace: 'nowrap',
    width: `${progression}%`,
  }),
});

export const IapPollStats: React.FC<IIapPollStatsProps> = (props) => {
  const { sx, appId, iapPollId } = props;
  const { t } = useTranslation();
  const iapPollResultsQuery = useIapPollResultsQuery({
    appId,
    iapPollId,
    body: {
      groupBy: 'articleId',
    },
  });
  const articleQueries = useArticlesQueries(
    iapPollResultsQuery.data?.votes?.map(({ _id: articleId }) => ({
      appId,
      articleId,
    })),
    { enabled: !!iapPollResultsQuery.data?.votes?.length },
  );

  const iapPollResults = iapPollResultsQuery.data;
  const totalCount = iapPollResults?.votes?.length ?? 0;
  const totalPoints =
    iapPollResults?.votes?.reduce((acc, vote) => acc + vote.totalPoints, 0) ??
    0;
  const isEmpty = !iapPollResultsQuery.isLoading && !totalCount;

  return (
    <ContentBlock sx={sx}>
      {iapPollResultsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={iapPollResultsQuery.error} />
        </ContentBlock.Body>
      ) : iapPollResultsQuery.isLoading ? (
        <ContentBlock>
          <ContentBlock.Body first>
            {/* TODO: use skeletons */}
            {t('common.loading')}
          </ContentBlock.Body>
        </ContentBlock>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('IapPollStats.empty.title')} />
      ) : (
        <>
          <ContentBlock.Title>{t('IapPollStats.title')}</ContentBlock.Title>

          <ContentBlock.Body>
            <div {...stylex.props(styles.results)}>
              {iapPollResults?.votes?.map((vote, voteIndex) => (
                <div key={vote._id} {...stylex.props(styles.result)}>
                  <Text
                    sx={styles.progression(
                      round(((vote.totalPoints ?? 0) / totalPoints) * 100, 1),
                    )}
                    variant="label"
                    size="md"
                  >
                    <QuerySkeleton
                      query={articleQueries[voteIndex]}
                      length={{ min: 24, max: 64 }}
                    >
                      {articleQueries[voteIndex].data?.title ?? vote.articleId}
                    </QuerySkeleton>{' '}
                    {t('IapPollStats.label', {
                      progression: round(
                        ((vote.totalPoints ?? 0) / totalPoints) * 100,
                        1,
                      ),
                      points: vote.totalPoints,
                    })}
                  </Text>
                </div>
              ))}
            </div>
          </ContentBlock.Body>
        </>
      )}
    </ContentBlock>
  );
};
